import {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {
    AdminNotification,
    ContentfulNotificationsConfig,
    fetchContentfulNotifications,
    NOTIFICATIONS_REFRESH_INTERVAL_MS,
    readNotificationIds,
    saveReadNotificationIds
} from "./contentfulNotifications"

interface UseAdminNotificationsOptions {
    accountId: number
    config: ContentfulNotificationsConfig
}

export const useAdminNotifications = ({accountId, config}: UseAdminNotificationsOptions) => {
    const {spaceId, environment, deliveryToken, contentType} = config
    const [notifications, setNotifications] = useState<AdminNotification[]>([])
    const [readIds, setReadIds] = useState<Set<string>>(() => readNotificationIds(accountId))
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [error, setError] = useState(false)
    const lastFetchedAtRef = useRef(0)
    const abortControllerRef = useRef<AbortController | null>(null)
    const requestInFlightRef = useRef(false)

    useEffect(() => {
        setReadIds(readNotificationIds(accountId))
    }, [accountId])

    const load = useCallback(async (force = false) => {
        if (!force && requestInFlightRef.current) return
        if (!force && Date.now() - lastFetchedAtRef.current < NOTIFICATIONS_REFRESH_INTERVAL_MS) return

        abortControllerRef.current?.abort()
        const controller = new AbortController()
        abortControllerRef.current = controller
        requestInFlightRef.current = true
        setError(false)
        if (lastFetchedAtRef.current) setIsRefreshing(true)
        else setIsLoading(true)

        try {
            const nextNotifications = await fetchContentfulNotifications(
                {spaceId, environment, deliveryToken, contentType},
                controller.signal
            )
            setNotifications(nextNotifications)
            lastFetchedAtRef.current = Date.now()
        } catch (requestError) {
            if ((requestError as {name?: string})?.name !== "AbortError") setError(true)
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null
                requestInFlightRef.current = false
                setIsLoading(false)
                setIsRefreshing(false)
            }
        }
    }, [contentType, deliveryToken, environment, spaceId])

    useEffect(() => {
        lastFetchedAtRef.current = 0
        setNotifications([])
        void load(true)

        const intervalId = window.setInterval(() => {
            if (document.visibilityState === "visible") void load()
        }, NOTIFICATIONS_REFRESH_INTERVAL_MS)
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") void load()
        }
        document.addEventListener("visibilitychange", handleVisibilityChange)

        return () => {
            window.clearInterval(intervalId)
            document.removeEventListener("visibilitychange", handleVisibilityChange)
            abortControllerRef.current?.abort()
            abortControllerRef.current = null
            requestInFlightRef.current = false
        }
    }, [load])

    const updateReadIds = useCallback((nextReadIds: Set<string>) => {
        setReadIds(nextReadIds)
        saveReadNotificationIds(accountId, nextReadIds)
    }, [accountId])

    const markAsRead = useCallback((notificationId: string) => {
        setReadIds((currentReadIds) => {
            if (currentReadIds.has(notificationId)) return currentReadIds
            const nextReadIds = new Set([...Array.from(currentReadIds), notificationId])
            saveReadNotificationIds(accountId, nextReadIds)
            return nextReadIds
        })
    }, [accountId])

    const markAllAsRead = useCallback(() => {
        updateReadIds(new Set([...Array.from(readIds), ...notifications.map((notification) => notification.id)]))
    }, [notifications, readIds, updateReadIds])

    const unreadCount = useMemo(
        () => notifications.reduce((count, notification) => count + (readIds.has(notification.id) ? 0 : 1), 0),
        [notifications, readIds]
    )

    return {
        notifications,
        readIds,
        unreadCount,
        isLoading,
        isRefreshing,
        error,
        refresh: () => load(true),
        refreshIfStale: () => load(false),
        markAsRead,
        markAllAsRead
    }
}
